import { useEffect } from 'react';
import { fmtTime } from '../lib/time';
import { useAppStore } from '../state/store';
import { shadeColor } from './colors';

/**
 * «Quando partire?»: ombra dello stesso percorso partendo ogni 30 minuti
 * nelle prossime 6 ore. Un tocco su una barra riprogramma la partenza.
 */
export function DepartureSweep() {
  const plan = useAppStore((s) => s.plan);
  const planDepartMs = useAppStore((s) => s.planDepartMs);
  const sweep = useAppStore((s) => s.sweep);
  const sweepStatus = useAppStore((s) => s.sweepStatus);
  const runSweep = useAppStore((s) => s.runSweep);
  const setDepartMs = useAppStore((s) => s.setDepartMs);

  useEffect(() => {
    if (plan && sweep === null && sweepStatus === 'idle') void runSweep();
  }, [plan, sweep, sweepStatus, runSweep]);

  if (!plan || planDepartMs === null) return null;

  const points = sweep ?? [];
  const dayPoints = points.filter((p) => !p.night);
  const best = dayPoints.reduce<(typeof points)[number] | null>(
    (acc, p) => (acc === null || p.shadeFrac > acc.shadeFrac + 1e-6 ? p : acc),
    null,
  );
  const currentPct = Math.round(plan.chosen.shadeFrac * 100);
  const gainPp = best ? Math.round(best.shadeFrac * 100) - currentPct : 0;

  return (
    <section className="card sweep" aria-label="Quando partire">
      <div className="row">
        <div className="grow">
          <div className="t-headline">Quando partire?</div>
          <div className="t-sub">Ombra di questo percorso nelle prossime 6 ore</div>
        </div>
        {sweepStatus === 'loading' && <span className="mini-spinner" aria-label="Calcolo…" />}
      </div>

      {sweepStatus === 'error' && (
        <p className="t-cap">
          Non riesco a calcolarlo ora.{' '}
          <button type="button" className="link" onClick={() => void runSweep()}>
            Riprova
          </button>
        </p>
      )}

      {points.length > 0 && (
        <>
          <div className="sweep__bars" role="list">
            {points.map((p, i) => {
              const pct = Math.round(p.shadeFrac * 100);
              const isCurrent = i === 0;
              const isBest = best !== null && p.tMs === best.tMs && !isCurrent && gainPp >= 3;
              return (
                <button
                  key={p.tMs}
                  type="button"
                  role="listitem"
                  className={`sweep__bar${isCurrent ? ' sweep__bar--current' : ''}${isBest ? ' sweep__bar--best' : ''}`}
                  title={`${fmtTime(p.tMs)} · ${p.night ? 'notte' : `${pct}% ombra`}`}
                  aria-label={`Partenza alle ${fmtTime(p.tMs)}: ${p.night ? 'notte' : `${pct}% di ombra`}`}
                  onClick={() => setDepartMs(p.tMs)}
                >
                  <span className="sweep__pct t-num">{isCurrent || isBest ? `${pct}%` : ''}</span>
                  <span className="sweep__col">
                    <span
                      className="sweep__fill"
                      style={{
                        height: `${Math.max(6, pct)}%`,
                        background: p.night ? 'var(--label-3)' : shadeColor(p.shadeFrac),
                      }}
                    />
                  </span>
                  <span className="sweep__t t-num">{i % 2 === 0 ? fmtTime(p.tMs) : ''}</span>
                </button>
              );
            })}
          </div>
          {best && gainPp >= 3 && best.tMs !== planDepartMs && (
            <p className="sweep__tip">
              Partendo alle <strong>{fmtTime(best.tMs)}</strong> avresti il{' '}
              <strong>{Math.round(best.shadeFrac * 100)}%</strong> di ombra (+{gainPp} punti). Tocca
              una barra per riprogrammare.
            </p>
          )}
          {best && gainPp < 3 && (
            <p className="sweep__tip">
              L’orario scelto è già tra i migliori: spostare la partenza cambierebbe poco.
            </p>
          )}
        </>
      )}
    </section>
  );
}
